import {
  type CreateServerRequest,
  type CreateServerResponse,
  type ListServersResponse,
} from "@woven/contracts";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useControlApiClient } from "../../lib/hooks";

export type ServersState =
  | { status: "loading" }
  | { status: "ready"; data: ListServersResponse }
  | { status: "error"; message: string };

export interface CreateServerResult {
  submit: (input: CreateServerRequest) => Promise<CreateServerResponse | null>;
  submitting: boolean;
  error: string | null;
  result: CreateServerResponse | null;
}

/** Loads the caller's servers, with retry. */
export function useServers() {
  const client = useControlApiClient();
  const [revision, setRevision] = useState(0);
  const scope = useMemo(() => ({ client, revision }), [client, revision]);
  const [snapshot, setSnapshot] = useState<{ scope: typeof scope; state: ServersState } | null>(
    null
  );
  const reload = useCallback(() => setRevision((value) => value + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    scope.client.listServers(controller.signal).then(
      (data) => {
        if (!controller.signal.aborted) setSnapshot({ scope, state: { status: "ready", data } });
      },
      (error) => {
        if (!controller.signal.aborted) {
          setSnapshot({ scope, state: { status: "error", message: errorMessage(error) } });
        }
      }
    );
    return () => controller.abort();
  }, [scope]);

  const state: ServersState = snapshot?.scope === scope ? snapshot.state : { status: "loading" };
  return { state, reload };
}

/** Creates a server through the control API placeholder. */
export function useCreateServer(): CreateServerResult {
  const client = useControlApiClient();
  const scope = useMemo(() => ({ client }), [client]);
  const sessionRef = useRef<{
    scope: typeof scope;
    controller: AbortController | null;
  } | null>(null);
  const [snapshot, setSnapshot] = useState<{
    scope: typeof scope;
    submitting: boolean;
    error: string | null;
    result: CreateServerResponse | null;
  } | null>(null);

  useEffect(() => {
    const session = { scope, controller: null as AbortController | null };
    sessionRef.current = session;
    return () => {
      sessionRef.current = null;
      session.controller?.abort();
    };
  }, [scope]);

  const submit = useCallback(
    async (input: CreateServerRequest) => {
      const session = sessionRef.current;
      if (!session || session.scope !== scope || session.controller) return null;
      const controller = new AbortController();
      session.controller = controller;
      setSnapshot({ scope, submitting: true, error: null, result: null });
      try {
        const response = await scope.client.createServer(input, controller.signal);
        if (sessionRef.current !== session || controller.signal.aborted) return null;
        setSnapshot({ scope, submitting: false, error: null, result: response });
        return response;
      } catch (cause) {
        if (sessionRef.current === session && !controller.signal.aborted) {
          setSnapshot({ scope, submitting: false, error: errorMessage(cause), result: null });
        }
        return null;
      } finally {
        if (session.controller === controller) session.controller = null;
      }
    },
    [scope]
  );

  return {
    submit,
    submitting: snapshot?.scope === scope && snapshot.submitting,
    error: snapshot?.scope === scope ? snapshot.error : null,
    result: snapshot?.scope === scope ? snapshot.result : null,
  };
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "Unexpected error";
}
